import { useEffect, useState, useCallback } from 'react';
import { Sparkles, Check, AlertCircle, AlertTriangle, User, LogOut, LogIn, ExternalLink, CreditCard, Clock } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  useLicenseStore,
  ProBadge,
  useAuth,
  AuthDialog,
  useSubscriptionStore,
  selectIsPro,
  selectIsInGracePeriod,
  selectGracePeriodDaysRemaining,
  selectAuthStale,
  UpgradeDialog,
  useLicenseCache,
} from '../../../../core/licensing';
import { startServerTrial } from '../../../../core/licensing/services/entitlementTokenService';
import { isAuthConfigured } from '../../../../core/licensing/services/authService';
import { recordAuthIntentSource } from '../../../../core/licensing/services/signInIntent';
import { TRIAL_DURATION_DAYS } from '../../../../core/licensing/services/trialService';
import { trackTrialRequested } from '../../../../core/licensing/services/trialRequestedEvent';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
const LEGAL_URL = 'https://jamsql.com/legal/';

export function LicenseSettingsTab() {
  const { isPro: isLicensePro, getTrialState, getTrialDaysRemaining, startTrial, startTrialFromServer } = useLicenseStore();
  const { user, isAuthenticated, isLoading, signOut, isConfigured } = useAuth();
  const {
    subscription,
    fetchSubscription,
    openCustomerPortal,
    error: subscriptionError,
  } = useSubscriptionStore();

  // Derived subscription state
  const isSubscriptionPro = useSubscriptionStore(selectIsPro);
  const isInGracePeriod = useSubscriptionStore(selectIsInGracePeriod);
  const gracePeriodDays = useSubscriptionStore(selectGracePeriodDaysRemaining);
  const authStale = useSubscriptionStore(selectAuthStale);

  // Offline license cache state
  const { cacheStatus } = useLicenseCache();

  // Combined Pro check (license OR subscription)
  const hasProAccess = isLicensePro() || isSubscriptionPro || isInGracePeriod;

  // Trial state
  // Server-backed trials: the server is the authority on eligibility
  // Show "Start Trial" for users who haven't started OR who previously skipped
  // (the server will check if they've already used their trial)
  const trialState = getTrialState();
  const isTrialActive = trialState === 'active';
  const isTrialEligible = trialState === 'not-started' || trialState === 'skipped';
  const isTrialExpired = trialState === 'expired';
  const trialDaysRemaining = getTrialDaysRemaining();

  // Calculate "last verified" display
  const getLastVerifiedText = () => {
    if (!cacheStatus?.lastValidated) return null;
    const now = new Date();
    const lastValidated = new Date(cacheStatus.lastValidated);
    const diffMs = now.getTime() - lastValidated.getTime();
    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

    if (diffDays === 0) return 'Verified today';
    if (diffDays === 1) return 'Verified yesterday';
    return `Verified ${diffDays} days ago`;
  };

  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isStartingTrial, setIsStartingTrial] = useState(false);
  const [trialError, setTrialError] = useState<string | null>(null);

  const handleOpenLegal = () => {
    if (window.electronAPI?.openExternalUrl) {
      void window.electronAPI.openExternalUrl(LEGAL_URL);
      return;
    }

    window.open(LEGAL_URL, '_blank', 'noopener,noreferrer');
  };

  // Fetch subscription when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchSubscription();
    }
  }, [isAuthenticated, user, fetchSubscription]);

  const handleSignOut = async () => {
    await signOut();
  };

  const handleManageSubscription = async () => {
    setIsOpeningPortal(true);
    await openCustomerPortal();
    setIsOpeningPortal(false);
  };

  const handleStartTrial = useCallback(async () => {
    // Same funnel stage as the onboarding dialog's button (round-2 review):
    // the button only renders for a signed-in user.
    trackTrialRequested('settings', true);
    setTrialError(null);
    setIsStartingTrial(true);

    try {
      // Check if auth is configured
      if (!isAuthConfigured()) {
        // Fall back to local trial for dev/testing
        startTrial();
        setIsStartingTrial(false);
        return;
      }

      const result = await startServerTrial();

      if (!result.success) {
        setTrialError(result.error || 'Failed to start trial');
        setIsStartingTrial(false);
        return;
      }

      // Update local state with server trial data
      if (result.trial) {
        startTrialFromServer(
          new Date(result.trial.started_at),
          new Date(result.trial.expires_at),
          user?.id,
          result.trial.is_new
        );
      }

      // Cache the signed token for offline use
      if (result.token && window.electronAPI?.licenseCache) {
        try {
          await window.electronAPI.licenseCache.updateCacheWithSignedToken(result.token);
        } catch (cacheErr) {
          console.warn('[LicenseSettings] Failed to cache trial token:', cacheErr);
        }
      }
    } catch (err) {
      console.error('[LicenseSettings] Error starting trial:', err);
      setTrialError(err instanceof Error ? err.message : 'Failed to start trial');
    } finally {
      setIsStartingTrial(false);
    }
  }, [startTrial, startTrialFromServer, user?.id]);

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Account Section */}
      {isConfigured && (
        <div className="space-y-3">
          <div className="text-sm font-medium">Account</div>

          <div className="rounded-md border border-border bg-background p-4">
            {isAuthenticated && user ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div className="grid gap-0.5">
                    <span className="text-sm font-medium">{user.email}</span>
                    <span className="text-xs text-muted-foreground">
                      Signed in
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSignOut}
                  disabled={isLoading}
                  data-testid="sign-out-button"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                    <User className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="grid gap-0.5">
                    <span className="text-sm font-medium">Not signed in</span>
                    <span className="text-xs text-muted-foreground">
                      Sign in to manage your subscription
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    recordAuthIntentSource('settings');
                    setShowAuthDialog(true);
                  }}
                  disabled={isLoading}
                  data-testid="sign-in-button"
                >
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Subscription Section (when authenticated) */}
      {isAuthenticated && subscription && subscription.status !== 'none' && (
        <div className="space-y-3">
          <div className="text-sm font-medium">Subscription</div>

          <div className="rounded-md border border-border bg-background p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-primary" />
                <div className="grid gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Pro Subscription</span>
                    {isSubscriptionPro && <ProBadge size="sm" />}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Status: {subscription.status}
                    {subscription.cancelAtPeriodEnd && ' (cancels at period end)'}
                  </div>
                </div>
              </div>
            </div>

            {/* Grace Period Warning */}
            {isInGracePeriod && (
              <div className="flex items-center gap-2 text-sm text-warning">
                <AlertCircle className="h-4 w-4" />
                <span>
                  Payment past due. {gracePeriodDays} days remaining to update payment.
                </span>
              </div>
            )}

            {/* Subscription Details */}
            {subscription.currentPeriodEnd && (
              <div className="text-xs text-muted-foreground">
                {subscription.cancelAtPeriodEnd
                  ? `Access until: ${formatDate(subscription.currentPeriodEnd)}`
                  : `Renews: ${formatDate(subscription.currentPeriodEnd)}`}
              </div>
            )}

            {/* Last Verified Indicator */}
            {getLastVerifiedText() && (
              <div className="text-xs text-muted-foreground" data-testid="last-verified-text">
                {getLastVerifiedText()}
              </div>
            )}

            {/* Manage Subscription Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleManageSubscription}
              disabled={isOpeningPortal}
              data-testid="manage-subscription-button"
            >
              {isOpeningPortal ? (
                <>
                  <RandomDotmSpinner size={16} className="mr-2" />
                  Opening...
                </>
              ) : (
                <>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Manage Subscription
                </>
              )}
            </Button>

            {authStale && (
              <div className="flex items-center gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Session expired</span>
                <span className="text-amber-400/50 dark:text-amber-500/50">·</span>
                <button
                  onClick={async () => {
                    await signOut();
                    recordAuthIntentSource('settings');
                    setShowAuthDialog(true);
                  }}
                  className="inline-flex items-center gap-1 font-medium hover:underline"
                >
                  <LogIn className="h-3.5 w-3.5" />
                  Sign in again
                </button>
              </div>
            )}
            {subscriptionError && !authStale && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>{subscriptionError}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Current Plan Section */}
      <div className="space-y-3">
        <div className="text-sm font-medium">Current Plan</div>

        <div className="rounded-md border border-border bg-background p-4">
          <div className="flex items-center gap-3">
            {isTrialActive && !isSubscriptionPro ? (
              <>
                <Clock className="h-5 w-5 text-primary" />
                <div className="grid gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Pro Trial</span>
                    <ProBadge size="sm" />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining
                  </div>
                </div>
              </>
            ) : hasProAccess ? (
              <>
                <Sparkles className="h-5 w-5 text-primary" />
                <div className="grid gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Pro</span>
                    {isTrialActive && (
                      <span className="text-xs text-muted-foreground">
                        — Trial ({trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} left)
                      </span>
                    )}
                    <ProBadge size="sm" />
                  </div>
                </div>
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5 text-muted-foreground" />
                <div className="grid gap-0.5">
                  <span className="font-medium">Personal (Free)</span>
                  <div className="text-xs text-muted-foreground">
                    Core functionality enabled
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Upgrade Section for Trial Users (hide when already subscribed) */}
      {isTrialActive && !isSubscriptionPro && (
        <div className="space-y-3">
          <div className="text-sm font-medium">Keep Pro Forever</div>

          <div className="rounded-md border border-border bg-muted/30 p-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Enjoying your trial? Subscribe now to keep all Pro features after your trial ends.
            </p>

            <Button
              onClick={() => setShowUpgradeDialog(true)}
              className="w-full"
              data-testid="upgrade-from-trial-button"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Subscribe to Pro
            </Button>
          </div>
        </div>
      )}

      {/* Upgrade Section (only shown when no Pro access) */}
      {!hasProAccess && (
        <div className="space-y-3">
          <div className="text-sm font-medium">Upgrade to Pro</div>

          <div className="rounded-md border border-border bg-muted/30 p-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Unlock session management, full query history, and more.
            </p>

            <ul className="space-y-2">
              {[
                'Create and manage multiple sessions',
                'Full query history per session',
                'Resume any session exactly where you left off',
              ].map((benefit, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>

            {/* Trial Error */}
            {trialError && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>{trialError}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              {/* Start Trial Button (only for eligible authenticated users) */}
              {isAuthenticated && isTrialEligible && (
                <Button
                  variant="outline"
                  onClick={handleStartTrial}
                  disabled={isStartingTrial}
                  className="flex-1"
                  data-testid="start-trial-button"
                >
                  {isStartingTrial ? (
                    <>
                      <RandomDotmSpinner size={16} className="mr-2" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Clock className="mr-2 h-4 w-4" />
                      Start {TRIAL_DURATION_DAYS}-Day Trial
                    </>
                  )}
                </Button>
              )}

              {/* Subscribe Button */}
              <Button
                onClick={() => setShowUpgradeDialog(true)}
                className={isAuthenticated && isTrialEligible ? 'flex-1' : 'w-full'}
                data-testid="upgrade-button"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Subscribe to Pro
              </Button>
            </div>

            {/* Trial expired notice */}
            {isTrialExpired && (
              <p className="text-xs text-muted-foreground">
                Your trial has ended. Subscribe to continue using Pro features.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Legal */}
      <div className="space-y-3">
        <div className="text-sm font-medium">Legal</div>

        <div className="rounded-md border border-border bg-background p-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            End User License Agreement (EULA), third-party notices, and license text.
          </p>

          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenLegal}
            data-testid="open-legal-page-button"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            View Legal
          </Button>
        </div>
      </div>

      {/* Auth Dialog */}
      <AuthDialog
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
      />

      {/* Upgrade Dialog */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
      />
    </div>
  );
}
